import { action, computed, observable, runInAction } from 'mobx'
import PostModel from '@/models/PostModel.js'
import { debounce } from 'lodash-es'
import { getImage } from '@/utils/media.js'
import { UPLOAD_KEYS } from '@/constants/uploads.js'
import { POST_TYPES, PRICING_MODELS } from '@/constants/post.js'
import { addMinutes, compareDesc, isPast } from 'date-fns'
import {
  POST_CATEGORY_FILTER_COMBOBOX_DEFAULT_ITEM,
  POST_PUBLISHED_FILTER_COMBOBOX,
  POST_PUBLISHED_FILTER_STATE,
  POST_PURCHASED_FILTER_COMBOBOX,
  POST_PURCHASED_FILTER_STATE,
  POST_TYPE_FILTER_COMBOBOX,
} from '@/constants/postFilters.js'

export default class PostsRepository {
  rootStore = null

  #feedCancelToken = null
  #storeCancelToken = null

  @observable accessor isInitialized = false
  @observable.shallow accessor posts = []
  @observable accessor typeFilter = ''
  @observable accessor categoryFilter = -1
  @observable accessor publishedFilter = ''
  @observable accessor purchasedFilter = ''

  constructor(rootStore) {
    this.rootStore = rootStore
    this.resetFilters()
  }

  initFeed = debounce(async () => this.#loadFeedPosts(), 100)

  initStore = debounce(async address => this.#loadStorePosts(address), 100)

  @action
  reset = () => {
    this.#feedCancelToken?.abort()
    this.#storeCancelToken?.abort()
    this.isInitialized = false
    this.posts = []
    this.resetFilters()
  }

  get analytics() {
    return this.rootStore.analyticsStore
  }

  get api() {
    return this.rootStore.apiStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  get uploads() {
    return this.rootStore.uploadsStore
  }

  get users() {
    return this.rootStore.repositories.usersRepository
  }

  @computed
  get isFiltering() {
    return (
      this.isFilteringByType ||
      this.isFilteringByCategory ||
      this.isFilteringByPublished ||
      this.isFilteringByPurchased
    )
  }

  @computed
  get isFilteringByType() {
    return this.typeFilter !== POST_TYPE_FILTER_COMBOBOX[0].value
  }

  @computed
  get isFilteringByCategory() {
    return this.categoryFilter !== Number(POST_CATEGORY_FILTER_COMBOBOX_DEFAULT_ITEM.value)
  }

  @computed
  get isFilteringByPublished() {
    return this.publishedFilter !== POST_PUBLISHED_FILTER_COMBOBOX[0].value
  }

  @computed
  get isFilteringByPurchased() {
    return this.purchasedFilter !== POST_PURCHASED_FILTER_COMBOBOX[0].value
  }

  @computed
  get selectedCategory() {
    if (!this.router.isStorePage || !this.hasPosts) return null

    return this.posts[0].user.categories.get(this.categoryFilter) ?? null
  }

  @computed
  get filteredPosts() {
    if (!this.isFiltering) return this.posts

    const mustBePublished = this.publishedFilter === POST_PUBLISHED_FILTER_STATE
    const mustBePurchased = this.purchasedFilter === POST_PURCHASED_FILTER_STATE

    return this.posts.filter(x => {
      if (this.isFilteringByType && !x.type.startsWith(this.typeFilter)) return false
      if (this.isFilteringByCategory && x.categoryId !== this.categoryFilter) return false
      if (this.isFilteringByPublished && x.published !== mustBePublished) return false
      if (this.isFilteringByPurchased && x.canAccess !== mustBePurchased) return false

      return true
    })
  }

  @computed
  get postCount() {
    return this.posts.length
  }

  @computed
  get hasPosts() {
    return this.postCount > 0
  }

  @computed
  get hasFilteredPosts() {
    return this.filteredPosts.length > 0
  }

  @computed
  get canFilterByCategory() {
    return this.router.isStorePage && this.categoryFilterCombobox?.length > 1
  }

  @computed
  get categoryFilterCombobox() {
    if (!this.router.isStorePage || !this.hasPosts) return null

    const user = this.posts[0].user
    const categories = user.isAuthUser ? user.categoriesArray : user.publishedCategoriesArray
    const items = categories.map(x => x.comboboxItem)

    items.unshift(POST_CATEGORY_FILTER_COMBOBOX_DEFAULT_ITEM)

    return items
  }

  @action
  resetFilters = () => {
    this.typeFilter = POST_TYPE_FILTER_COMBOBOX[0].value
    this.categoryFilter = Number(POST_CATEGORY_FILTER_COMBOBOX_DEFAULT_ITEM.value)
    this.publishedFilter = POST_PUBLISHED_FILTER_COMBOBOX[0].value
    this.purchasedFilter = POST_PURCHASED_FILTER_COMBOBOX[0].value
  }

  @action
  onPostTypeFilterChange = x => {
    this.typeFilter = x?.value ?? POST_TYPE_FILTER_COMBOBOX[0].value
  }

  @action
  onPostCategoryFilterChange = x => {
    this.categoryFilter = Number(x?.value ?? POST_CATEGORY_FILTER_COMBOBOX_DEFAULT_ITEM.value)
  }

  @action
  onPostPublishedFilterChange = x => {
    this.publishedFilter = x?.value ?? POST_PUBLISHED_FILTER_COMBOBOX[0].value
  }

  @action
  onPostPurchasedFilterChange = x => {
    this.purchasedFilter = x?.value ?? POST_PURCHASED_FILTER_COMBOBOX[0].value
  }

  createPostFromForm = async values => {
    if (!this.auth.user.isAuthUser) return null

    values.id = crypto.randomUUID().replace(/-/g, '').toLowerCase().substring(0, 64)

    let videoPreviewId = null
    let videoContentId = null
    let imageContentId = null

    try {
      if (values.type === POST_TYPES.IMAGE) {
        if (values.pricingModel === PRICING_MODELS.ONE_TIME) {
          imageContentId = values.id
          const success = await this.api.uploadImagesBeforePostCreation(
            imageContentId,
            values.imageContent
          )

          if (success) values.imageContent = imageContentId
          else {
            console.error('[Posts] Error uploading image content')
            await this.#deleteStalePostMedia(videoPreviewId, videoContentId, imageContentId)

            return null
          }
        }

        values.startTime = ''
      } else if (values.type === POST_TYPES.VIDEO) {
        const upload = this.uploads.getUpload(UPLOAD_KEYS.VIDEO_CONTENT)

        if (upload) {
          await upload.waitUntilReady()

          if (upload.isUploaded) values.videoContent = videoContentId = upload.videoId
          else {
            console.error('[Posts] Error uploading video content')
            await this.#deleteStalePostMedia(videoPreviewId, videoContentId, imageContentId)

            return null
          }
        }

        values.startTime = ''
      } else {
        if (isPast(values.startTime)) values.startTime = new Date()
        values.endTime = addMinutes(values.startTime, values.durationMins)
      }

      if (values.imagePreview) values.imagePreview = await getImage(values.imagePreview)

      if (
        values.videoPreview &&
        !(values.type === POST_TYPES.IMAGE && values.pricingModel === PRICING_MODELS.FREE)
      ) {
        const upload = this.uploads.getUpload(UPLOAD_KEYS.VIDEO_PREVIEW)

        if (upload) {
          await upload.waitUntilReady()

          if (upload.isUploaded) values.videoPreview = videoPreviewId = upload.videoId
          else {
            console.error('[Posts] Error uploading video preview')
            await this.#deleteStalePostMedia(videoPreviewId, videoContentId, imageContentId)

            return null
          }
        }
      } else {
        await this.uploads.removeUpload(UPLOAD_KEYS.VIDEO_PREVIEW, true)
        values.videoPreview = ''
      }

      if (values.type !== POST_TYPES.IMAGE || values.pricingModel === PRICING_MODELS.FREE)
        values.imageContent = ''

      if (values.type !== POST_TYPES.VIDEO) {
        await this.uploads.removeUpload(UPLOAD_KEYS.VIDEO_CONTENT, true)
        values.videoContent = ''
      }

      const response = await this.api.createPost(values)

      if (!response?.data?.jwt) {
        await this.#deleteStalePostMedia(videoPreviewId, videoContentId, imageContentId)

        return null
      }

      this.analytics.createScheduledMeeting()?.then()

      // if (values.type === POST_TYPES.IMAGE) this.analytics.uploadImage()?.then()
      // else if (values.type === POST_TYPES.VIDEO) this.analytics.uploadVideo()?.then()

      return response.data.hash
    } catch (e) {
      console.error('[Posts] Error creating post', e)
      await this.#deleteStalePostMedia(videoPreviewId, videoContentId, imageContentId)

      return null
    }
  }

  @action
  removePost = id => {
    this.posts = this.posts.filter(x => x.id !== id)
  }

  #deleteStalePostMedia = async (videoPreviewId, videoContentId, imageContentId) => {
    const promises = []

    if (videoPreviewId) promises.push(this.api.deleteMedia(videoPreviewId, 'video'))
    if (videoContentId) promises.push(this.api.deleteMedia(videoContentId, 'video'))
    if (imageContentId) promises.push(this.api.deleteMedia(imageContentId, 'image'))

    return Promise.all(promises)
  }

  #loadFeedPosts = async () => {
    if (this.router.isStorePage) return

    this.#feedCancelToken = new AbortController()
    this.#storeCancelToken?.cancel()

    const response = await this.api.getFeedPosts(this.#feedCancelToken.signal)
    this.#feedCancelToken = null

    if (!response?.data || this.router.isStorePage) return

    if (!response.data.next) {
      // FIXME: backend server should add promeet as favorite if user has no favorite stores
      const promeetUser = await this.users.getPromeetUser()
      await promeetUser.toggleFavorite()

      return this.#loadFeedPosts()
    }

    runInAction(() => {
      this.posts = response.data.next.map(x => new PostModel(this.rootStore, x))
      this.isInitialized = true
    })
  }

  #loadStorePosts = async address => {
    if (this.router.isHomePage) return

    this.#storeCancelToken = new AbortController()
    this.#feedCancelToken?.cancel()

    // FIXME: should work via user handle and not his address to speed up loading
    const response = await this.api.getStorePosts(address, this.#storeCancelToken.signal)
    this.#storeCancelToken = null

    if (this.router.isHomePage) return

    if (response?.data?.next) {
      runInAction(() => {
        this.posts = response.data.next.map(x => new PostModel(this.rootStore, x))

        // FIXME: wtf is going on in backend
        this.posts.sort((a, b) => compareDesc(a.createdAt, b.createdAt))
      })

      if (this.hasPosts) await this.posts[0].user.updateCategories()
    }

    runInAction(() => {
      this.isInitialized = true
    })
  }
}
